/**
 * The A4 print stylesheet for the kitchen documents of §11.6.
 *
 * Three things rev7 and Planteen's own document standard ask for and this
 * sets: **A4**, **Raleway 10 pt**, **page numbers**.
 *
 * Raleway is declared the way `packages/emails/src/layout.ts` declares it —
 * `Raleway, Arial, …`, no web font fetched. A document that pulled a font
 * from a third party would make a kitchen print depend on the internet, and
 * the fallback is already the one the emails use. Where Raleway is installed
 * (Planteen's own machines) it renders in Raleway; everywhere else in Arial
 * at the same size. Logged as a default.
 *
 * 10 pt is the size, declared in points rather than pixels because the sheet
 * is a sheet of paper. The whole document is laid out in millimetres for the
 * same reason: an A4 page is 210 × 297 mm and 14 mm margins leave 182 × 269 mm
 * of it, which is what `KITCHEN_ROWS_FIRST` and `KITCHEN_ROWS_REST` in
 * `KitchenDoc.tsx` are counted against.
 */
export const PRINT_STYLES = `
@page { size: A4 portrait; margin: 14mm; }

.plt-print { font-family: Raleway, Arial, 'Helvetica Neue', Helvetica, sans-serif;
  font-size: 10pt; line-height: 1.35; color: #000; background: #e9ebe9; margin: 0;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.plt-print * { box-sizing: border-box; }

.plt-print .toolbar { position: sticky; top: 0; display: flex; gap: 10px; align-items: center;
  padding: 8px 12px; background: #fff; border-bottom: 1px solid #cfd6d1; font-size: 9pt; }
.plt-print .toolbar button, .plt-print .toolbar a { font: inherit; padding: 5px 11px; border-radius: 4px;
  border: 1px solid #cfd6d1; background: #fff; color: #17211b; cursor: pointer; text-decoration: none; }
.plt-print .toolbar .spacer { margin-left: auto; color: #5d6b62; }

.plt-print .sheet { width: 210mm; min-height: 297mm; padding: 14mm; margin: 8mm auto; background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,.18); position: relative; display: flex; flex-direction: column; }

.plt-print .band { border: 1.2pt solid #000; padding: 2mm 3mm; margin: 0 0 2.5mm;
  font-weight: 700; letter-spacing: .06em; text-transform: uppercase; font-size: 11pt; }
.plt-print .band.void { background: #000; color: #fff; font-size: 14pt; text-align: center; }

.plt-print h1 { font-size: 13pt; margin: 0 0 1mm; }
.plt-print h2 { font-size: 10.5pt; margin: 4mm 0 1.5mm; }
.plt-print .sub { font-size: 9pt; color: #333; margin: 0 0 3mm; }
/* The one sentence under the title that says what the sheet is for: K2's
   reason, K3's "the previous sheet is void", K4's "do not make this". */
.plt-print .lead { font-size: 10.5pt; margin: 0 0 3mm; }

.plt-print dl.header { display: grid; grid-template-columns: auto 1fr auto 1fr; gap: 1mm 4mm;
  margin: 0 0 3.5mm; border-top: .6pt solid #000; border-bottom: .6pt solid #000; padding: 2mm 0; }
.plt-print dl.header dt { color: #333; }
.plt-print dl.header dd { margin: 0; font-weight: 600; }

.plt-print table { width: 100%; border-collapse: collapse; }
.plt-print th, .plt-print td { text-align: left; padding: 1.4mm 2mm; border-bottom: .4pt solid #999;
  vertical-align: top; }
.plt-print thead th { border-bottom: .8pt solid #000; font-size: 8.5pt; text-transform: uppercase;
  letter-spacing: .04em; }
.plt-print td.num, .plt-print th.num { text-align: right; font-variant-numeric: tabular-nums; }
.plt-print td.allergens { font-size: 8.5pt; }
.plt-print .gf { font-weight: 700; }
/* K5 groups by station and dish and splits by order (§11.6): the station
   heading sits inside the first dish row of its group so the group cannot be
   separated from its name by a page break, and an order line is indented
   under the dish it belongs to. */
.plt-print .station { font-size: 8.5pt; text-transform: uppercase; letter-spacing: .05em;
  color: #333; margin-bottom: .6mm; }
.plt-print td.sub-row { padding-left: 6mm; color: #333; }
.plt-print .block ul { margin: 0; padding-left: 5mm; }
.plt-print .gap { font-style: italic; color: #444; }

.plt-print .totals { margin-top: 3mm; border-top: .8pt solid #000; padding-top: 2mm; font-weight: 600; }
.plt-print .block { margin-top: 4mm; border: .6pt solid #000; padding: 2.5mm 3mm; }
.plt-print .block h2 { font-size: 10pt; margin: 0 0 1.5mm; }

.plt-print .label-sheet { display: grid; grid-template-columns: 1fr 1fr; gap: 4mm; }
.plt-print .label { border: .8pt solid #000; padding: 3mm; min-height: 38mm; }
.plt-print .label .ref { font-size: 8.5pt; letter-spacing: .04em; }
.plt-print .label .dish { font-size: 12pt; font-weight: 700; margin: 1mm 0; }
.plt-print .label .al { font-size: 8pt; }

.plt-print .foot { margin-top: auto; padding-top: 3mm; border-top: .4pt solid #000;
  display: flex; gap: 6mm; font-size: 8pt; color: #333; }
.plt-print .foot .page { margin-left: auto; font-weight: 600; color: #000; }

@media print {
  .plt-print { background: #fff; }
  .plt-print .toolbar { display: none; }
  .plt-print .sheet { width: auto; min-height: 0; margin: 0; padding: 0; box-shadow: none;
    page-break-after: always; break-after: page; }
  .plt-print .sheet:last-child { page-break-after: auto; break-after: auto; }
}
`
